import { useAuthStore } from '@stores/authStore'
import { environment } from '@config/environment'
import type { User } from '../types/auth'

/**
 * Hook for authentication management
 * Provides auth state and actions with additional utilities
 */
export function useAuth() {
  const {
    user,
    isLoading,
    login: storeLogin,
    logout: storeLogout,
    setUser,
    setLoading,
  } = useAuthStore()

  // Enhanced login function with better error handling
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      await storeLogin(email, password)
      return true
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }

  // Enhanced logout function
  const logout = async (): Promise<void> => {
    try {
      await storeLogout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  // Check if user is authenticated
  const isAuthenticated = !!user

  // Check if user has specific role
  const hasRole = (role: 'admin' | 'user'): boolean => {
    return user?.role === role || user?.isAdmin === (role === 'admin')
  }

  // Check if user is admin
  const isAdmin = hasRole('admin')

  // Get user initials
  const getUserInitials = (): string => {
    if (!user) return ''
    
    if (user.initials) return user.initials
    
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    }
    
    if (user.name) {
      const names = user.name.split(' ')
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase()
      }
      return names[0][0].toUpperCase()
    }
    
    return user.email?.[0]?.toUpperCase() || '?'
  }

  // Get user display name
  const getUserDisplayName = (): string => {
    if (!user) return ''
    
    if (user.name) return user.name
    
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    
    return user.email || ''
  }

  // Check if running in fake mode
  const isFakeMode = environment.isFakeMode()

  // Get connection state display
  const getConnectionStateDisplay = (): string => {
    if (!user?.userConnectionState) return 'Inconnu'
    
    switch (user.userConnectionState) {
      case 'connected':
        return 'Connecté'
      case 'notConnected':
        return 'Déconnecté'
      default:
        return 'Inconnu'
    }
  }

  // Update user profile
  const updateProfile = async (updates: Partial<User>): Promise<boolean> => {
    if (!user) return false

    try {
      setLoading(true)
      
      // In fake mode, just update locally
      if (isFakeMode) {
        const updatedUser = { ...user, ...updates }
        setUser(updatedUser)
        return true
      }

      // TODO: Implement actual API call for profile update
      // const updatedUser = await apiUpdateProfile(user.uid, updates)
      // setUser(updatedUser)
      
      // For now, just update locally
      const updatedUser = { ...user, ...updates }
      setUser(updatedUser)
      
      return true
    } catch (error) {
      console.error('Profile update failed:', error)
      return false
    } finally {
      setLoading(false)
    }
  }

  // Change password
  const changePassword = async (): Promise<boolean> => {
    if (!user) return false

    try {
      setLoading(true)
      
      // In fake mode, always succeed
      if (isFakeMode) {
        return true
      }

      // TODO: Implement actual password change API call
      // await apiChangePassword(user.uid, currentPassword, newPassword)
      
      return true
    } catch (error) {
      console.error('Password change failed:', error)
      return false
    } finally {
      setLoading(false)
    }
  }

  // Refresh user data
  const refreshUser = async (): Promise<boolean> => {
    if (!user?.uid) return false

    try {
      setLoading(true)
      
      // In fake mode, return current user
      if (isFakeMode) {
        return true
      }

      // TODO: Implement actual user refresh API call
      // const refreshedUser = await apiGetUser(user.uid)
      // setUser(refreshedUser)
      
      return true
    } catch (error) {
      console.error('User refresh failed:', error)
      return false
    } finally {
      setLoading(false)
    }
  }

  return {
    // State
    user,
    isLoading,
    isAuthenticated,
    isAdmin,
    isFakeMode,
    
    // Actions
    login,
    logout,
    updateProfile,
    changePassword,
    refreshUser,
    
    // Utilities
    hasRole,
    getUserInitials,
    getUserDisplayName,
    getConnectionStateDisplay,
  }
}